/**
 * Dynamic resolution governor.
 *
 * The one lever that buys a weak GPU a playable frame rate without taking a
 * single effect away from it. Everything in the pipeline that costs real money
 * on integrated graphics — the forward pass, the MRT prepass, GTAO, contact
 * shadows, SSR, TAA, motion blur, the volumetric composite — is fill-bound, so
 * its cost is very close to linear in the internal pixel count. Rendering at
 * 0.72x is 48% of the pixels and very close to 48% of the frame, while the
 * *scene* is unchanged: same shadows, same AO, same shafts, same reflections,
 * same materials, same draw calls. Dropping an effect instead changes what the
 * game looks like; dropping resolution changes how sharp it is, and TAA plus
 * the composite's sharpen filter hide most of that.
 *
 * This is how every console shooter holds its frame target, and it is the only
 * technique here that is *self-limiting*: a machine that already makes the
 * target never leaves 1.0, so a capable GPU renders exactly the frame it
 * rendered before this file existed.
 *
 * ---------------------------------------------------------------------------
 * WHY THE MEASUREMENT IS NOT JUST "IS THE FRAME OVER BUDGET"
 * ---------------------------------------------------------------------------
 * rAF deltas are clamped by vsync. A machine rendering in 8 ms on a 60 Hz panel
 * reports 16.7 ms, which is indistinguishable from a machine that is genuinely
 * 16.7 ms of work — so a naive "step up when we're comfortably under budget"
 * rule can never restore resolution after a dip, because the clamp keeps the
 * measurement pinned just above a 20 ms target forever.
 *
 * So the governor learns the display's refresh period from the fastest frames
 * it has ever seen (`_floorMs`) and treats "sitting on the vsync floor" as
 * proof of headroom, independent of the budget. Uncapped contexts (the perf
 * harness runs with --disable-gpu-vsync) never sit on a floor, so the ordinary
 * budget comparison drives those.
 *
 * Spikes are excluded rather than averaged: a shader compile, a GC pause or an
 * alt-tab is not a reason to drop resolution, and a mean would let one 400 ms
 * hitch outvote a hundred good frames. The window statistic is a median.
 *
 * ---------------------------------------------------------------------------
 * IT ALSO CHECKS WHETHER LOWERING THE RESOLUTION IS DOING ANYTHING
 * ---------------------------------------------------------------------------
 * Resolution only buys frame time while the frame is fill-bound. Once the cost
 * has moved into draw-call submission and vertex throughput — which is exactly
 * what happens at the lower tiers, where the effects are already cheap but the
 * scene still has ~900 draw calls and millions of triangles in it — shrinking
 * the buffer costs sharpness and returns nothing.
 *
 * So every downward step is an experiment with a verdict (see `_probeFrom`): if
 * it fails to buy 6%, the step is undone and the governor stops stepping down
 * for ~30 seconds. A player on a geometry-bound machine keeps a sharp image at
 * the frame rate they were going to get anyway, instead of a soft one at the
 * same frame rate.
 */

/**
 * Scale steps, high to low. Quantised on purpose: each change reallocates the
 * HDR/ping/gbuffer/AO/TAA targets and resets the TAA history, so the governor
 * must have somewhere to *settle* rather than dithering by 1% forever.
 * 0.58 is the floor — below that the sharpen filter stops rescuing text and
 * the reticle starts to crawl.
 */
export const SCALE_STEPS = [1.0, 0.92, 0.85, 0.78, 0.72, 0.66, 0.62, 0.58];

const WINDOW = 45; // frames per decision window
const SETTLE = 24; // frames ignored after a change (realloc + TAA refill)
const COOLDOWN_DOWN = 30; // frames before another drop is allowed
const COOLDOWN_UP = 150; // frames before a climb is allowed — climbing is optimistic

export class ResolutionGovernor {
  /**
   * @param {object}  opts
   * @param {number}  [opts.targetMs]   frame-time budget to hold
   * @param {number}  [opts.minScale]   hard floor
   * @param {number}  [opts.maxScale]   hard ceiling — 1.0, i.e. native
   * @param {number}  [opts.startScale] where to begin (the preset's own scale)
   * @param {boolean} [opts.enabled]
   */
  constructor(opts = {}) {
    this.enabled = opts.enabled !== false;
    // 20.8 ms = 48 fps. The budget is deliberately tighter than the 25 ms that
    // "40 fps" literally asks for: the governor reacts to a *median*, and a
    // median sitting exactly on 25 ms means half of all frames are below 40.
    this.targetMs = opts.targetMs ?? 20.8;
    this.minScale = opts.minScale ?? SCALE_STEPS[SCALE_STEPS.length - 1];
    this.maxScale = opts.maxScale ?? 1.0;

    this._samples = new Float32Array(WINDOW);
    this._sorted = new Float32Array(WINDOW);
    this._n = 0;
    this._write = 0;
    this._settle = SETTLE;
    this._cooldown = 0;
    /** Fastest credible frame seen, i.e. the vsync period if there is one. */
    this._floorMs = Infinity;

    // ---- "did that actually help?" ---------------------------------------
    // MEASURED on an Iris Xe at the `balanced` tier: 1632x918 ran at 25.4 ms and
    // 1113x626 — 47% of the pixels — ran at 25.0 ms. The frame at that tier is
    // not fill-bound any more, it is bound by submitting 935 draw calls and
    // 6.65 M triangles, and no amount of resolution fixes that. Left alone, the
    // governor walks all the way to the floor chasing a target it cannot reach
    // and hands the player a soft image for a 1.6% gain.
    //
    // So every downward step is treated as an EXPERIMENT: keep the median from
    // before it, and if the step did not buy a real improvement, put the
    // resolution back and stop trying. That is the difference between adaptive
    // resolution and just turning the resolution down.
    this._probeFrom = 0;
    /** Has a downward step ever paid for itself? Null = not yet tested. */
    this.fillBound = null;
    /** Frames to wait before re-testing after a step that did not help. */
    this._retryIn = 0;

    // A preset's own renderScale is where we START, not a ceiling. If the
    // machine turns out to have headroom the governor climbs toward native —
    // a laptop that was guessed as `balanced` but is actually keeping up should
    // end up rendering at full resolution, not stuck at 80% forever.
    this.index = this._nearestStep(opts.startScale ?? this.maxScale);
    this.scale = SCALE_STEPS[this.index];
    /** Diagnostics. */
    this.medianMs = 0;
    this.changes = 0;
    this.vsyncLocked = false;
  }

  _nearestStep(s) {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < SCALE_STEPS.length; i++) {
      const d = Math.abs(SCALE_STEPS[i] - s);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /** Clamp the ceiling (e.g. the player picked a different preset). */
  setMaxScale(s) {
    this.maxScale = s;
    const cap = this._nearestStep(s);
    if (this.index < cap) {
      this.index = cap;
      this.scale = SCALE_STEPS[cap];
    }
  }

  /** Force a scale and stop adapting for a moment (preset switch, resize). */
  reset(scale = this.maxScale) {
    this.index = this._nearestStep(scale);
    this.scale = SCALE_STEPS[this.index];
    this._n = 0;
    this._write = 0;
    this._settle = SETTLE;
    this._cooldown = 0;
    return this.scale;
  }

  /**
   * Feed one frame time.
   * @param   {number} ms  wall-clock delta for the frame just presented
   * @returns {number|null} a new scale to apply, or null to keep the current one
   */
  submit(ms) {
    if (!this.enabled) return null;
    // A frame this long is a stall (shader compile, GC, tab restore), not a
    // steady-state cost. Feeding it in would drop resolution for something
    // resolution cannot fix.
    if (!(ms > 0) || ms > 400) return null;

    if (this._settle > 0) {
      this._settle--;
      return null;
    }
    if (this._cooldown > 0) this._cooldown--;

    this._samples[this._write] = ms;
    this._write = (this._write + 1) % WINDOW;
    if (this._n < WINDOW) this._n++;
    if (this._n < WINDOW) return null;

    // Median of the window. WINDOW is small and fixed, so an insertion sort of
    // a preallocated copy costs less than any cleverer structure and allocates
    // nothing.
    const s = this._sorted;
    s.set(this._samples);
    for (let i = 1; i < WINDOW; i++) {
      const v = s[i];
      let j = i - 1;
      while (j >= 0 && s[j] > v) {
        s[j + 1] = s[j];
        j--;
      }
      s[j + 1] = v;
    }
    const med = s[WINDOW >> 1];
    this.medianMs = med;

    // The refresh period is learned from the fastest WINDOW MEDIAN seen, not
    // the fastest single frame: rAF occasionally fires two callbacks almost
    // back to back, and one 2 ms sample would otherwise pin the floor below
    // anything achievable and permanently convince the governor it has no
    // headroom. A median cannot be moved by one outlier.
    if (med < this._floorMs) this._floorMs = med;

    // Sitting within 1.5 ms of that floor means the presenter is the limit, not
    // us: there is headroom here that the clock cannot show.
    //
    // The `med <= targetMs` half is load-bearing, not belt-and-braces. On the
    // very first decision window the floor IS this median, so the distance test
    // passes by construction — without the budget check a machine whose first
    // measurement was 35 ms would read as "pinned to the refresh rate" and
    // immediately climb. Being at the floor only means headroom if the floor is
    // somewhere we are happy to be.
    this.vsyncLocked = med <= this._floorMs + 1.5 && med <= this.targetMs;

    if (this._retryIn > 0) this._retryIn--;

    // ---- verdict on the last downward experiment --------------------------
    // `_probeFrom` is the median from immediately before the step. A step that
    // shaved less than 6% off it did not work: the frame is bound by something
    // resolution does not touch (draw-call submission, vertex throughput, the
    // main thread). Give the pixels back and stop stepping down until the
    // workload has had a chance to change.
    if (this._probeFrom > 0) {
      const gain = 1 - med / this._probeFrom;
      this._probeFrom = 0;
      if (gain < 0.06) {
        this.fillBound = false;
        this._retryIn = 1800; // ~30 s at 60 fps before testing again
        if (this.index > 0) {
          this.index--;
          return this._commit(COOLDOWN_DOWN);
        }
        return null;
      }
      this.fillBound = true;
    }

    if (this._cooldown > 0) return null;

    // ---- too slow: give up pixels ----------------------------------------
    if (med > this.targetMs * 1.06 && !this.vsyncLocked) {
      // Not fill-bound and not yet due for a re-test: dropping resolution would
      // cost sharpness and return nothing. Hold.
      if (this.fillBound === false && this._retryIn > 0) return null;
      if (this.index < SCALE_STEPS.length - 1 && SCALE_STEPS[this.index + 1] >= this.minScale) {
        this.index++;
        this._probeFrom = med;
        return this._commit(COOLDOWN_DOWN);
      }
      return null;
    }

    // ---- comfortable: take pixels back ------------------------------------
    // Either we are pinned to the refresh rate, or we are far enough under
    // budget that one step up (about +18% cost) still lands inside it.
    const roomy = this.vsyncLocked || med < this.targetMs * 0.74;
    if (roomy && this.index > 0 && SCALE_STEPS[this.index - 1] <= this.maxScale) {
      this.index--;
      return this._commit(COOLDOWN_UP);
    }
    return null;
  }

  _commit(cooldown) {
    this.scale = SCALE_STEPS[this.index];
    this._cooldown = cooldown;
    this._settle = SETTLE;
    this._n = 0;
    this._write = 0;
    this.changes++;
    return this.scale;
  }
}
